import React, { FC, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useI18n } from '~Hooks';
import { i18nProps } from '~Types';
import { formatTimePassed } from '../../utils';

interface Props {
  compStatus: {
    isUpcoming: boolean;
    isOngoing: boolean;
    isCompleted: boolean;
  };
  preText?: string;
  endDate: string;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const calculateTimeLeft = (endDate: string): TimeLeft | {} => {
  const difference = +new Date(endDate) - +new Date();
  if (difference > 0) {
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  }
  return {};
};

const ParagraphWrapper = styled.div<{ blink: boolean }>`
  margin-right: 4px;
  animation: ${({ blink }) => (blink ? 'blink-animation 1s steps(2, start) infinite' : 'none')};
`;

const formatTimeLeft = (
  timeLeft: TimeLeft,
  isUpcoming: boolean,
  i18n: i18nProps,
): string | boolean => {
  const { days, hours, minutes, seconds } = timeLeft;
  const leftTranslation = isUpcoming ? '' : ` ${i18n('Competitions.Countdown.Left')}`;

  if (days > 0) {
    return `${days} ${i18n('Competitions.Countdown.Days', days)}${leftTranslation}`;
  }
  if (hours > 0) {
    return `${hours} ${i18n('Competitions.Countdown.Hours')} ${i18n(
      'Competitions.Countdown.And',
    )} ${minutes} ${i18n('Competitions.Countdown.Minutes')}${leftTranslation}`;
  }
  if (minutes > 0) {
    return `${minutes} ${i18n('Competitions.Countdown.Minutes')} ${i18n(
      'Competitions.Countdown.And',
    )} ${seconds} ${i18n('Competitions.Countdown.Seconds')}${leftTranslation}`;
  }
  if (seconds > 0) {
    return `${seconds} ${i18n('Competitions.Countdown.Seconds')}${leftTranslation}`;
  }
  return false;
};

const useTimeLeft = (endDate: string): TimeLeft | {} => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft | {}>(calculateTimeLeft(endDate));

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(endDate));
    }, 1000);

    // Cleanup timer on component unmount
    return () => clearInterval(timer);
  }, [endDate]);

  return timeLeft;
};

const CountDown: FC<Props> = ({ compStatus, preText, endDate }) => {
  const { i18n } = useI18n();
  const timeLeft = useTimeLeft(endDate) as TimeLeft;

  const isHandyBreakpoint = window.innerWidth <= 600;

  const isBlinking = Boolean(
    timeLeft.seconds && timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0,
  );

  const formattedTimeLeft = formatTimeLeft(timeLeft, compStatus.isUpcoming, i18n);

  // We check if the time is present or not, if not we show the time passed
  const timeString = formattedTimeLeft
    ? `${formattedTimeLeft}`
    : `${formatTimePassed(endDate, i18n)}`;

  return (
    <ParagraphWrapper blink={isBlinking}>
      <Paragraph level={isHandyBreakpoint ? 5 : 4} color="var(--colorBlueGray200)">
        {preText ? `${preText} ${timeString}` : timeString}
      </Paragraph>
    </ParagraphWrapper>
  );
};

export default CountDown;
